import { useEffect, useRef, useState } from 'react'
import { Upload } from 'lucide-react'
import { signInWithOAuth } from '@/utils/authOAuth'
import { markQuickUploadAuth, requestQuickUploadDialog } from '@/lib/quickUpload'
import { getPublicGamePackages } from '@/lib/communityCreditPricing'
import { Mp4Icon, VeoIcon, YoutubeIcon } from '@/components/icons/FootageIcons'
import { maxVideoUploadLabel, validateVideoFileSize } from '@/lib/videoUploadLimits'
import { isValidMatchVideoFile } from '@/lib/dualMp4Footage'
import { VIDEO_LINK_HELP } from '@/components/academy-schedule/academySchedule.helpers'

function GoogleIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" aria-hidden className="shrink-0">
      <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
      <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
      <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
      <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
    </svg>
  )
}

function MicrosoftIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 23 23" aria-hidden className="shrink-0">
      <path fill="#f35325" d="M1 1h10v10H1z" />
      <path fill="#81bc06" d="M12 1h10v10H12z" />
      <path fill="#05a6f0" d="M1 12h10v10H1z" />
      <path fill="#ffba08" d="M12 12h10v10H12z" />
    </svg>
  )
}

/**
 * Footage choice shown after sign-in, on the same surface as the questions.
 */
export function GameUploadCard({
  source,
  onSourceChange,
  youtubeUrl,
  onYoutubeUrlChange,
  fileName = '',
  onFile,
  uploadStatus = 'idle',
  uploadProgress = 0,
  grow = false,
}) {
  const inputRef = useRef(null)
  const [fileError, setFileError] = useState('')

  const takeFile = (next) => {
    if (!next || !onFile) return
    if (!isValidMatchVideoFile(next) && !String(next.type || '').startsWith('video/')) {
      setFileError('Choose a video file, such as an MP4 or MOV.')
      return
    }
    try {
      validateVideoFileSize(next)
    } catch (err) {
      setFileError(err?.message || 'That video is too large.')
      return
    }
    setFileError('')
    onFile(next)
  }

  return (
    <div id="upload" className={`w-full text-left text-[#1c1917] ${grow ? 'flex flex-1 flex-col lg:block' : ''}`}>
      <div className="grid grid-cols-2 gap-2" role="tablist" aria-label="How to add the game">
        <button
          type="button"
          role="tab"
          aria-selected={source === 'video'}
          onClick={() => onSourceChange('video')}
          className={`flex min-h-12 items-center justify-center gap-2 rounded-2xl border px-3 text-sm font-semibold focus:outline-none focus-visible:ring-2 focus-visible:ring-[#071018] ${
            source === 'video'
              ? 'border-[#071018] bg-[#071018] text-white'
              : 'border-[#e7e5e4] bg-white text-[#44403c] hover:border-[#d6d3d1]'
          }`}
        >
          <Mp4Icon className="h-5 w-5" />
          Video file
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={source === 'youtube'}
          onClick={() => onSourceChange('youtube')}
          className={`flex min-h-12 items-center justify-center gap-1.5 rounded-2xl border px-3 text-sm font-semibold focus:outline-none focus-visible:ring-2 focus-visible:ring-[#071018] ${
            source === 'youtube'
              ? 'border-[#071018] bg-[#071018] text-white'
              : 'border-[#e7e5e4] bg-white text-[#44403c] hover:border-[#d6d3d1]'
          }`}
        >
          <YoutubeIcon className="h-5 w-5" />
          <VeoIcon className="h-5 w-5" />
          Link
        </button>
      </div>

      {source === 'youtube' ? (
        <div className="mt-3">
          <label htmlFor="landing-youtube" className="mb-1.5 block text-sm font-medium text-[#1c1917]">
            YouTube or Veo link
          </label>
          <input
            id="landing-youtube"
            type="url"
            inputMode="url"
            value={youtubeUrl}
            onChange={(event) => onYoutubeUrlChange(event.target.value)}
            placeholder="Paste a YouTube or Veo link"
            className="h-12 w-full rounded-2xl border border-[#e7e5e4] bg-white px-3 text-base text-[#1c1917] placeholder:text-[#78716c] focus:border-[#071018] focus:outline-none focus:ring-2 focus:ring-[#071018]/15"
          />
          <p className="mt-2 text-sm leading-relaxed text-[#57534e]">{VIDEO_LINK_HELP}</p>
        </div>
      ) : (
        <div className={`mt-3 ${grow ? 'flex flex-1 flex-col lg:block' : ''}`}>
          <button
            type="button"
            onClick={() => {
              if (onFile) inputRef.current?.click()
            }}
            onDragOver={(event) => event.preventDefault()}
            onDrop={(event) => {
              event.preventDefault()
              if (onFile) takeFile(event.dataTransfer.files?.[0])
            }}
            className={`${grow ? 'flex-1 lg:flex-none' : ''} flex min-h-[88px] w-full flex-col items-center justify-center rounded-2xl border border-dashed border-[#d6d3d1] bg-[#fafaf9] px-4 py-3 sm:min-h-[104px] sm:py-4 text-center hover:border-[#071018] hover:bg-white focus:outline-none focus-visible:ring-2 focus-visible:ring-[#071018]`}
          >
            {grow && !fileName ? (
              <span className="mb-2 flex h-10 w-10 items-center justify-center rounded-full bg-white text-[#071018] shadow-[0_6px_16px_-8px_rgba(7,16,24,0.35)] ring-1 ring-[#e7e5e4] lg:hidden">
                <Upload className="h-5 w-5" aria-hidden />
              </span>
            ) : null}
            <p className="max-w-full break-all text-base font-semibold text-[#1c1917]" title={fileName || undefined}>
              {fileName ? fileName : 'Drop a video or browse'}
            </p>
            {uploadStatus === 'uploading' ? (
              <div className="mt-3 w-full max-w-[240px]">
                <div className="h-1.5 overflow-hidden rounded-full bg-[#e7e5e4]">
                  <div
                    className="h-full rounded-full bg-[#0c3a42]"
                    style={{ width: `${Math.max(4, Math.min(100, uploadProgress))}%` }}
                  />
                </div>
                <p className="mt-1.5 text-sm font-medium text-[#0c3a42]">Uploading {uploadProgress}%</p>
              </div>
            ) : uploadStatus === 'done' ? (
              <p className="mt-1 text-sm font-medium text-[#0c3a42]">Uploaded</p>
            ) : (
              <p className="mt-1 text-sm text-[#57534e]">
                MP4, MOV, and more, up to {maxVideoUploadLabel()}
              </p>
            )}
          </button>
          {onFile ? (
            <input
              ref={inputRef}
              type="file"
              accept="video/*"
              className="hidden"
              onChange={(event) => {
                takeFile(event.target.files?.[0])
                event.target.value = ''
              }}
            />
          ) : null}
          {fileError ? (
            <p role="alert" className="mt-2 text-sm text-red-700">
              {fileError}
            </p>
          ) : null}
        </div>
      )}

    </div>
  )
}

/** Google and Microsoft sign-in. Disabled until the video has finished uploading, because the redirect stops it. */
export function QuickAuthButtons({ disabled = false, error = '', onBeforeAuth }) {
  const [authLoading, setAuthLoading] = useState(null)
  const [authError, setAuthError] = useState(error)

  useEffect(() => {
    setAuthError(error)
  }, [error])

  const startAuth = async (provider) => {
    if (disabled || authLoading) return
    setAuthError('')
    setAuthLoading(provider)
    onBeforeAuth?.()
    markQuickUploadAuth()
    const { error: oauthError } = await signInWithOAuth(
      provider === 'microsoft' ? 'azure' : 'google',
      `${window.location.origin}/signup/callback`,
    )
    if (oauthError) {
      setAuthError(oauthError.message || 'Could not start sign-in. Try again.')
      setAuthLoading(null)
    }
  }

  const buttonClass =
    'flex min-h-[52px] w-full items-center justify-center gap-2.5 rounded-2xl border border-[#e7e5e4] bg-white text-[15px] font-semibold text-[#1c1917] hover:border-[#d6d3d1] hover:bg-[#fafaf9] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#1c1917] disabled:cursor-not-allowed disabled:opacity-50'

  return (
    <div>
      <div className="space-y-2.5">
        <button
          type="button"
          onClick={() => startAuth('google')}
          disabled={disabled || !!authLoading}
          className={buttonClass}
        >
          <GoogleIcon />
          {authLoading === 'google' ? 'Opening Google…' : 'Continue with Google'}
        </button>
        <button
          type="button"
          onClick={() => startAuth('microsoft')}
          disabled={disabled || !!authLoading}
          className={buttonClass}
        >
          <MicrosoftIcon />
          {authLoading === 'microsoft' ? 'Opening Microsoft…' : 'Continue with Microsoft'}
        </button>
      </div>
      {authError ? (
        <p role="alert" className="mt-3 text-sm text-red-700">
          {authError}
        </p>
      ) : null}
    </div>
  )
}

/** Dark-page action. Opens the upload flow straight away; sign-in comes after the game is added. */
export function LandingUploadGate() {
  const firstGameSavings = getPublicGamePackages().find((pack) => pack.credits === 1)?.savings

  return (
    <div className="relative inline-flex max-w-full">
      <div
        aria-hidden
        className="pointer-events-none absolute inset-2 rounded-full"
        style={{
          background: 'rgba(113,205,217,0.45)',
          filter: 'blur(28px)',
        }}
      />
      <button
        type="button"
        onClick={requestQuickUploadDialog}
        className="relative inline-flex min-h-[52px] max-w-full items-center justify-center rounded-full px-7 text-[#071018] shadow-[0_18px_40px_-12px_rgba(113,205,217,0.7),inset_0_1px_0_rgba(255,255,255,0.45)] transition-transform duration-200 hover:scale-[1.02] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#8fe0ec] focus-visible:ring-offset-2 focus-visible:ring-offset-[#07080c] motion-reduce:transition-none motion-reduce:hover:scale-100"
        style={{ background: 'linear-gradient(180deg, #8fe0ec 0%, #71cdd9 58%, #5dbac6 100%)' }}
      >
        <span className="whitespace-nowrap text-base font-bold leading-none tracking-[-0.02em]">Upload your full game</span>
        {firstGameSavings ? (
          <span className="sr-only">, {firstGameSavings}% off your first game</span>
        ) : null}
      </button>
      {firstGameSavings ? (
        <span
          aria-hidden
          className="pointer-events-none absolute -top-2.5 right-4 whitespace-nowrap rounded-full bg-[#071018] px-2.5 py-[3px] text-[11px] font-bold leading-none tracking-[-0.01em] text-white shadow-[0_6px_14px_-6px_rgba(0,0,0,0.8)] ring-1 ring-[#8fe0ec]/70"
        >
          <span className="text-[#8fe0ec]">{firstGameSavings}% off</span> your first game
        </span>
      ) : null}
    </div>
  )
}
